import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ChevronLeft, ChevronRight, Radio, X } from "lucide-react";
import { VIDEOS } from "@/data/news";
import { ShareRowDark } from "@/components/site/Share";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shareUrlFor = (slug) => (slug ? `${BACKEND}/api/share/${slug}` : undefined);

function VideoPlayer({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!video) return null;
  // Use youtube-nocookie for stricter privacy AND to sidestep some referrer-based
  // "Video unavailable" errors seen on custom domains. Autoplay must be muted
  // by browser policy, and playsinline lets Shorts play inside the iframe.
  const yid = video.youtube_id || "";
  const src = yid
    ? `https://www.youtube-nocookie.com/embed/${yid}?autoplay=1&mute=1&rel=0&playsinline=1&modestbranding=1`
    : "";
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[130] bg-black/85 flex items-center justify-center px-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="video-player-modal"
      >
        <motion.div
          initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
          transition={{ duration: 0.25 }}
          className="relative w-full max-w-[1100px] aspect-video bg-black shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={onClose} data-testid="video-player-close"
            aria-label="बंद करें"
            className="absolute -top-4 -right-4 z-10 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#D92525] hover:text-white transition-colors shadow-xl">
            <X size={18} />
          </button>
          {src ? (
            <iframe
              title={video.title}
              src={src}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
              style={{ border: 0 }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60">वीडियो उपलब्ध नहीं</div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function VideoSection({ videos }) {
  // Prefer live data from backend; fall back to static demo videos only if none seeded
  const data = videos && videos.length ? videos : VIDEOS;
  const scroller = useRef(null);
  const [active, setActive] = useState(null);
  const featured = data[0];
  const rest = data.slice(1);

  const scrollBy = (dir) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({ left: dir * 380, behavior: "smooth" });
  };

  const openVideo = (e, v) => {
    e.preventDefault();
    // Only open player if we have a YouTube id (real videos from backend)
    if (v.youtube_id || v.embed_url) setActive(v);
  };

  return (
    <section
      id="videos"
      data-testid="video-section"
      className="bg-[#0A0A0A] text-[#F4F4F0] border-t border-white/10"
    >
      {/* Big red announcement band so this section is impossible to miss */}
      <div className="bg-[#D92525] text-white border-b border-black/20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 md:py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
            <span className="font-sans-dev text-[11px] md:text-[12px] tracking-[0.28em] uppercase font-bold">
              लाइव · देनवा टीवी
            </span>
          </div>
          <div className="font-serif-dev italic text-[14px] md:text-[16px] hidden sm:block">
            स्क्रीन पर देखिए वो सब जो शब्दों से रह गया।
          </div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-sans-dev">
            <Radio size={14} /> ऑन एयर 24×7
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="eyebrow text-[#D92525] mb-3">वीडियो · देनवा टीवी</div>
            <h2 className="font-serif-dev font-black text-[38px] md:text-[54px] leading-[1.02]">
              देखिए, <span className="italic">जो पढ़ने से रह गया</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="video-scroll-left"
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-white/25 hover:bg-white/10 flex items-center justify-center"
              aria-label="पिछला"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="video-scroll-right"
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-white/25 hover:bg-white/10 flex items-center justify-center"
              aria-label="अगला"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Featured */}
          <motion.a
            href="#"
            onClick={(e) => openVideo(e, featured)}
            data-testid={`video-featured-${featured.id}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 lg:col-span-7 group relative block cursor-pointer"
          >
            <div className="relative thumb-wrap aspect-[16/10] border border-white/15">
              <img src={featured.thumb} alt={featured.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute top-5 left-5 eyebrow text-white/90 bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">
                {featured.category || featured.cat}
              </div>
              {(featured.duration || featured.dur) && (
                <div className="absolute bottom-5 right-5 font-sans-dev text-[12px] text-white/90 bg-black/50 backdrop-blur px-2.5 py-1 rounded-full">
                  {featured.duration || featured.dur}
                </div>
              )}
              <button
                aria-label="चलाएँ"
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/12 backdrop-blur-md border border-white/40 flex items-center justify-center group-hover:bg-[#D92525] transition-colors">
                  <Play size={30} className="translate-x-[3px]" fill="currentColor" />
                </span>
              </button>
            </div>
            <h3 className="mt-5 font-serif-dev font-bold text-[22px] md:text-[26px] leading-[1.2] headline-link">
              {featured.title}
            </h3>
            <div className="mt-4" onClick={(e) => e.stopPropagation()}>
              <ShareRowDark title={featured.title} url={shareUrlFor(featured.slug)} testid={`share-video-${featured.id}`} />
            </div>
          </motion.a>

          {/* Sidebar list */}
          <div className="col-span-12 lg:col-span-5">
            <div ref={scroller} className="flex lg:flex-col gap-5 overflow-x-auto lg:overflow-visible hide-scrollbar snap-x lg:snap-none">
              {rest.map((v, i) => (
                <motion.a
                  key={v.id}
                  href="#"
                  onClick={(e) => openVideo(e, v)}
                  data-testid={`video-item-${v.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="shrink-0 w-[300px] lg:w-auto group flex flex-col lg:flex-row gap-4 snap-start cursor-pointer"
                >
                  <div className="relative thumb-wrap w-full lg:w-40 aspect-video border border-white/15 shrink-0">
                    <img src={v.thumb} alt={v.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/20" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-10 h-10 rounded-full bg-white/15 backdrop-blur border border-white/40 flex items-center justify-center group-hover:bg-[#D92525] transition-colors">
                        <Play size={14} className="translate-x-[1px]" fill="currentColor" />
                      </span>
                    </span>
                    {(v.duration || v.dur) && (
                      <span className="absolute bottom-2 right-2 font-sans-dev text-[10px] bg-black/70 px-1.5 py-0.5 rounded">
                        {v.duration || v.dur}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="eyebrow text-[#D92525] mb-1.5">{v.category || v.cat}</div>
                    <h4 className="font-serif-dev font-semibold text-[15px] md:text-[16px] leading-snug headline-link">
                      {v.title}
                    </h4>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {active && <VideoPlayer video={active} onClose={() => setActive(null)} />}
    </section>
  );
}
